import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, formatApiError } from '@/lib/api';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { Target, Loader2, ArrowLeft, CircleHelp, Plus, Upload, Download } from 'lucide-react';

// Phase 3 — Role Benchmark V2 (PREVIEW). Deterministic ≤48-question benchmark
// against a Role Profile; 1-5 evidence scale + "No tengo evidencia suficiente";
// competency-level gaps only — NO overall fitness score by design.
const STATUS_META = {
  green:  { es: 'Cumple / Supera', en: 'Meets / Exceeds', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  yellow: { es: 'Brecha moderada', en: 'Moderate Gap',    cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  red:    { es: 'Brecha prioritaria', en: 'Priority Gap', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  insufficient_evidence: { es: 'Evidencia insuficiente', en: 'Insufficient Evidence', cls: 'bg-slate-100 text-slate-600 border-slate-300 border-dashed' },
};

// Founder-approved behavioral observation scale (H)
const SCALE = {
  1: { es: 'Muy por debajo de lo esperado', en: 'Well below expected' },
  2: { es: 'Por debajo de lo esperado', en: 'Below expected' },
  3: { es: 'Nivel esperado / adecuado', en: 'Expected / adequate level' },
  4: { es: 'Por encima de lo esperado', en: 'Above expected' },
  5: { es: 'Consistentemente por encima de lo esperado', en: 'Consistently above expected' },
};

export default function AdminRoleBenchmark() {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);

  const [bank, setBank] = useState(null);
  const [benchmarks, setBenchmarks] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rpId, setRpId] = useState('');
  const [evaluated, setEvaluated] = useState('');
  const [openBm, setOpenBm] = useState(null);     // full benchmark doc
  const [results, setResults] = useState(null);
  const [answers, setAnswers] = useState({});
  const [evaluatorName, setEvaluatorName] = useState(user?.name || '');
  const [evaluatorId, setEvaluatorId] = useState('');
  const [staffUsers, setStaffUsers] = useState([]);
  const [openDetail, setOpenDetail] = useState(null);

  const reload = () => {
    api.get('/admin/rb/bank').then((r) => setBank(r.data)).catch(() => {});
    api.get('/admin/rb/benchmarks').then((r) => setBenchmarks(r.data)).catch(() => {});
    adminApi.listRoleProfiles(false).then(setProfiles).catch(() => {});
    api.get('/admin/users', { params: { page: 1, page_size: 100 } })
      .then((r) => setStaffUsers((r.data?.items || []).filter((u) => ['hm_admin', 'hm_advisor', 'client_admin'].includes(u.role))))
      .catch(() => {});
  };
  useEffect(reload, []);

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/rb/bank/seed');
      toast.success(L(`Banco maestro v${data.version} listo — ${data.competency_count} competencias · ${data.indicator_count} indicadores`,
        `Master bank v${data.version} ready — ${data.competency_count} competencies · ${data.indicator_count} indicators`));
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const importBank = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/rb/bank/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(L(`BMIC importado — ${data.competency_count} competencias · ${data.indicator_count} indicadores`,
        `BMIC imported — ${data.competency_count} competencies · ${data.indicator_count} indicators`),
        data.issues?.length ? { description: L(`${data.issues.length} observaciones de importación`, `${data.issues.length} import issues`) } : undefined);
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const downloadReport = async (fmt) => {
    try {
      const r = await api.get(`/admin/rb/benchmarks/${openBm.id}/export/${fmt}`, {
        params: { lang: isES ? 'es' : 'en' }, responseType: 'blob',
      });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      const m = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '');
      a.href = href; a.download = (m && m[1]) || `esperado-vs-observado.${fmt}`; a.click();
      URL.revokeObjectURL(href);
    } catch { toast.error(L('No se pudo descargar el informe.', 'Report download failed.')); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/rb/benchmarks', {
        role_profile_id: rpId, evaluated_name: evaluated.trim(), evaluator_user_id: evaluatorId || undefined,
      });
      toast.success(L(`Evaluación generada — ${data.snapshot.item_ids.length} indicadores conductuales`, `Evaluation generated — ${data.snapshot.item_ids.length} behavioral indicators`));
      setEvaluated(''); reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'RB_TOO_MANY_COMPETENCIES') {
        toast.error(L('Demasiadas competencias para el benchmark', 'Too many competencies for the benchmark'), {
          description: d.message, duration: 9000,
        });
      } else toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const openBenchmark = async (id) => {
    setResults(null); setAnswers({});
    try {
      const { data } = await api.get(`/admin/rb/benchmarks/${id}`);
      setOpenBm(data);
      if (data.response) {
        const r = await api.get(`/admin/rb/benchmarks/${id}/results`);
        setResults(r.data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/rb/benchmarks/${openBm.id}/responses`, {
        evaluator_name: evaluatorName.trim(), answers,
      });
      toast.success(L('Respuestas registradas', 'Responses submitted'));
      await openBenchmark(openBm.id);
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const itemsByComp = useMemo(() => {
    if (!openBm) return [];
    const map = new Map();
    openBm.snapshot.items.forEach((it) => {
      if (!map.has(it.competency_code)) map.set(it.competency_code, []);
      map.get(it.competency_code).push(it);
    });
    return [...map.entries()];
  }, [openBm]);

  const answered = Object.keys(answers).length;
  const totalItems = openBm?.snapshot?.item_ids?.length || 0;
  const compName = (code) => {
    const n = openBm?.snapshot?.competency_names?.[code];
    return n ? (isES ? n.name_es : n.name) : code;
  };
  const statusBadge = (row) => {
    const m = STATUS_META[row.status];
    return (
      <Badge variant="outline" className={`text-xs ${m.cls}`} data-testid={`rb-status-${row.competency_code}`}>
        {isES ? m.es : m.en}
      </Badge>
    );
  };

  return (
    <>
      <Topbar
        title={L('Brechas de Competencias', 'Competency Gaps')}
        subtitle={L('TalentCompetency — observación conductual del evaluador vs. niveles requeridos del Perfil de Puesto',
          'TalentCompetency — evaluator behavioral observation vs. Role Profile required levels')}
      />
      <div className="px-6 lg:px-8 py-4 space-y-4" data-testid="rb-page">
        {!openBm && (
          <>
            <Card className="p-4 shadow-card flex flex-wrap items-center gap-3" data-testid="rb-bank-card">
              <Target className="h-5 w-5 text-primary" />
              {bank?.seeded ? (
                <span className="text-sm">
                  {L('Banco maestro', 'Master bank')}{' '}
                  <Badge variant="outline" className="mx-1" data-testid="rb-bank-label">
                    {bank.label || `v${bank.version}`}
                  </Badge>
                  <span className="tabular-nums" data-testid="rb-bank-stats">
                    {bank.competency_count} {L('competencias', 'competencies')} · {bank.indicator_count} {L('indicadores', 'indicators')}
                  </span>
                </span>
              ) : (
                <>
                  <span className="text-sm text-muted-foreground">{L('El banco maestro de indicadores aún no está inicializado.', 'The master indicator bank is not seeded yet.')}</span>
                  {isAdmin && (
                    <Button size="sm" onClick={seed} disabled={busy} data-testid="rb-seed-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L('Inicializar banco maestro', 'Seed master bank')}
                    </Button>
                  )}
                </>
              )}
              {isAdmin && (
                <label className="ml-auto">
                  <input type="file" accept=".xlsx" className="hidden" data-testid="rb-import-input"
                         onChange={(e) => { importBank(e.target.files?.[0]); e.target.value = ''; }} />
                  <Button size="sm" variant="outline" disabled={busy} asChild data-testid="rb-import-btn">
                    <span className="cursor-pointer"><Upload className="h-3.5 w-3.5 mr-1.5" />{L('Importar BMIC (.xlsx)', 'Import BMIC (.xlsx)')}</span>
                  </Button>
                </label>
              )}
            </Card>

            <Card className="p-4 shadow-card space-y-3" data-testid="rb-create-card">
              <p className="text-sm font-semibold">{L('Generar medición de Brechas de Competencias', 'Generate a Competency Gaps measurement')}</p>
              <div className="flex flex-wrap items-center gap-3">
                <Select value={rpId} onValueChange={setRpId}>
                  <SelectTrigger className="h-9 w-80" data-testid="rb-profile-select">
                    <SelectValue placeholder={L('Perfil de Puesto…', 'Role Profile…')} />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.position_title} · {(p.requirements || []).length} comp. {p.company_id ? '' : '(Global)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input className="h-9 w-64" value={evaluated} onChange={(e) => setEvaluated(e.target.value)}
                       placeholder={L('Persona evaluada', 'Evaluated person')} data-testid="rb-evaluated-input" />
                <Select value={evaluatorId} onValueChange={setEvaluatorId}>
                  <SelectTrigger className="h-9 w-64" data-testid="rb-evaluator-select">
                    <SelectValue placeholder={L('Evaluador designado…', 'Designated evaluator…')} />
                  </SelectTrigger>
                  <SelectContent>
                    {staffUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={create} disabled={busy || !rpId || !evaluated.trim() || !bank?.seeded} data-testid="rb-create-btn">
                  <Plus className="h-4 w-4 mr-1" /> {L('Generar', 'Generate')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {L('Máximo 12 competencias × 4 indicadores = 48 indicadores conductuales. Un perfil con más de 12 competencias requiere priorización — nunca se recortan en silencio. Sin puntaje global de idoneidad por diseño.',
                  'Maximum 12 competencies × 4 indicators = 48 behavioral indicators. A profile with more than 12 competencies requires prioritization — never silently truncated. No overall fitness score by design.')}
              </p>
            </Card>

            <Card className="shadow-card" data-testid="rb-list-card">
              <Table containerClassName="!overflow-visible">
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead>{L('Evaluado', 'Evaluated')}</TableHead>
                    <TableHead>{L('Perfil', 'Profile')}</TableHead>
                    <TableHead>{L('Banco', 'Bank')}</TableHead>
                    <TableHead>{L('Indicadores', 'Indicators')}</TableHead>
                    <TableHead>{L('Estado', 'Status')}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {benchmarks.map((b) => (
                    <TableRow key={b.id} data-testid={`rb-row-${b.id}`}>
                      <TableCell className="font-medium">{b.evaluated_name}</TableCell>
                      <TableCell className="text-sm">{b.snapshot?.role_profile?.position_title}</TableCell>
                      <TableCell className="text-xs tabular-nums">v{b.snapshot?.bank_version}</TableCell>
                      <TableCell className="text-xs tabular-nums">{(b.snapshot?.item_ids || []).length}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${b.status === 'completed' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : ''}`}>
                          {b.status === 'completed' ? L('Completado', 'Completed') : L('Abierto', 'Open')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => openBenchmark(b.id)} data-testid={`rb-open-${b.id}`}>
                          {b.status === 'completed' ? L('Ver resultados', 'View results') : L('Responder', 'Answer')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!benchmarks.length && (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground italic">
                      {L('Aún no hay benchmarks generados.', 'No benchmarks generated yet.')}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </>
        )}

        {openBm && (
          <>
            <Button variant="ghost" size="sm" onClick={() => { setOpenBm(null); setResults(null); }} data-testid="rb-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> {L('Volver', 'Back')}
            </Button>
            <Card className="p-4 shadow-card text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid="rb-detail-header">
              <span><b>{L('Evaluado', 'Evaluated')}:</b> {openBm.evaluated_name}</span>
              <span><b>{L('Perfil', 'Profile')}:</b> {openBm.snapshot.role_profile.position_title}</span>
              <span><b>{L('Banco', 'Bank')}:</b> v{openBm.snapshot.bank_version}</span>
              <span className="tabular-nums"><b>{L('Indicadores', 'Indicators')}:</b> {totalItems}</span>
            </Card>

            {!results && (
              <Card className="p-4 shadow-card space-y-5" data-testid="rb-answer-form">
                <div className="flex items-center gap-3">
                  <Input className="h-9 w-72" value={evaluatorName} onChange={(e) => setEvaluatorName(e.target.value)}
                         placeholder={L('Nombre del evaluador', 'Evaluator name')} data-testid="rb-evaluator-input" />
                  <span className="text-xs text-muted-foreground tabular-nums">{answered}/{totalItems}</span>
                </div>
                <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="rb-scale-legend">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                    {L('Escala de observación conductual', 'Behavioral observation scale')}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                    {[1, 2, 3, 4, 5].map((v) => (
                      <p key={v}><b className="tabular-nums mr-1">{v}</b>— {isES ? SCALE[v].es : SCALE[v].en}</p>
                    ))}
                    <p><b className="mr-1">N/E</b>— {L('No tengo evidencia suficiente (excluida del promedio)', "I don't have sufficient evidence (excluded from the mean)")}</p>
                  </div>
                </div>
                {itemsByComp.map(([code, items]) => (
                  <div key={code} className="space-y-2">
                    <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                      {compName(code)} — {L('Indicadores Conductuales', 'Behavioral Indicators')}
                    </p>
                    {items.map((it) => (
                      <div key={it.item_id} className="rounded-md border border-border p-3" data-testid={`rb-item-${it.item_id}`}>
                        {(it.focus_es || it.focus_en) && (
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-0.5">{isES ? it.focus_es : it.focus_en}</p>
                        )}
                        <p className="text-sm mb-2">{isES ? it.text_es : it.text_en}</p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <button key={v} type="button" data-testid={`rb-ans-${it.item_id}-${v}`}
                                    title={isES ? SCALE[v].es : SCALE[v].en}
                                    onClick={() => setAnswers((a) => ({ ...a, [it.item_id]: v }))}
                                    className={`h-8 w-8 rounded-md border text-sm font-medium transition
                                      ${answers[it.item_id] === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted/50'}`}>
                              {v}
                            </button>
                          ))}
                          <button type="button" data-testid={`rb-ans-${it.item_id}-noev`}
                                  onClick={() => setAnswers((a) => ({ ...a, [it.item_id]: 'no_evidence' }))}
                                  className={`h-8 px-2.5 rounded-md border text-xs transition flex items-center gap-1
                                    ${answers[it.item_id] === 'no_evidence' ? 'bg-slate-700 text-white border-slate-700' : 'border-dashed border-border text-muted-foreground hover:bg-muted/50'}`}>
                            <CircleHelp className="h-3 w-3" />
                            {L('No tengo evidencia suficiente', "I don't have sufficient evidence")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <Button onClick={submit} disabled={busy || answered < totalItems || !evaluatorName.trim()} data-testid="rb-submit-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L('Enviar respuestas', 'Submit responses')}
                </Button>
              </Card>
            )}

            {results && (
              <Card className="shadow-card" data-testid="rb-results">
                <div className="p-4 border-b border-border text-sm flex flex-wrap items-center gap-x-6 gap-y-2">
                  <span><b>{L('Evaluador', 'Evaluator')}:</b> {results.evaluator_name}</span>
                  {results.previous_evaluation && (
                    <span className="text-xs text-muted-foreground" data-testid="rb-prev-eval-note">
                      {L('Medición anterior', 'Prior measurement')}: {(() => { const d = new Date(results.previous_evaluation.submitted_at); const dd = String(d.getDate()).padStart(2, '0'), mm = String(d.getMonth() + 1).padStart(2, '0'); return isES ? `${dd}/${mm}/${d.getFullYear()}` : `${mm}/${dd}/${d.getFullYear()}`; })()}
                    </span>
                  )}
                  <span className="text-muted-foreground text-xs self-center">
                    {L('Resultados por competencia — sin puntaje global de idoneidad por diseño.',
                      'Competency-level results — no overall fitness score by design.')}
                  </span>
                  <span className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => downloadReport('pdf')} data-testid="rb-export-pdf">
                      <Download className="h-3.5 w-3.5" /> PDF
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => downloadReport('docx')} data-testid="rb-export-docx">
                      <Download className="h-3.5 w-3.5" /> Word
                    </Button>
                  </span>
                </div>
                <Table containerClassName="!overflow-visible">
                  <TableHeader className="bg-muted/40">
                    <TableRow>
                      <TableHead>{L('Competencia', 'Competency')}</TableHead>
                      <TableHead className="text-center">{L('Requerido', 'Required')}</TableHead>
                      <TableHead className="text-center">{L('Observado', 'Observed')}</TableHead>
                      <TableHead className="text-center">{L('Brecha vs. esperado', 'Gap vs. expected')}</TableHead>
                      <TableHead>{L('Estado', 'Status')}</TableHead>
                      <TableHead className="text-center">{L('Evidencia', 'Evidence')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.results.map((r) => (
                      <React.Fragment key={r.competency_code}>
                      <TableRow data-testid={`rb-result-${r.competency_code}`} className="cursor-pointer"
                                onClick={() => setOpenDetail(openDetail === r.competency_code ? null : r.competency_code)}>
                        <TableCell className="font-medium text-sm">
                          {isES ? r.name_es : r.name}
                          {r.previous_observed != null && (
                            <span className="block text-[11px] text-muted-foreground mt-0.5" data-testid={`rb-prev-${r.competency_code}`}>
                              {L('Variación vs. medición anterior', 'Change vs. prior measurement')}:
                              {r.delta_vs_previous != null ? (
                                <b className={r.delta_vs_previous >= 0 ? ' text-emerald-700' : ' text-rose-700'}>
                                  {' '}{r.delta_vs_previous > 0 ? '+' : ''}{r.delta_vs_previous}
                                </b>
                              ) : ' —'}
                              {' '}({L('anterior', 'prior')}: {r.previous_observed})
                            </span>
                          )}
                          {r.observed_level != null && (
                            <span className="block mt-1 w-40">
                              <span className="block h-1.5 rounded bg-muted relative">
                                <span className="absolute h-1.5 rounded bg-primary/30" style={{ width: `${(r.required_level / 5) * 100}%` }} />
                                <span className="absolute h-1.5 rounded bg-primary" style={{ width: `${(r.observed_level / 5) * 100}%` }} />
                              </span>
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center tabular-nums">{r.required_level}</TableCell>
                        <TableCell className="text-center tabular-nums" data-testid={`rb-observed-${r.competency_code}`}>
                          {r.observed_level ?? '—'}
                        </TableCell>
                        <TableCell className="text-center tabular-nums" data-testid={`rb-gap-${r.competency_code}`}>
                          {r.gap ?? '—'}
                        </TableCell>
                        <TableCell>{statusBadge(r)}</TableCell>
                        <TableCell className="text-center tabular-nums text-xs" data-testid={`rb-evidence-${r.competency_code}`}>
                          {r.evidence_count}/{r.evidence_total}
                        </TableCell>
                      </TableRow>
                      {openDetail === r.competency_code && (
                        <TableRow className="bg-muted/30">
                          <TableCell colSpan={6}>
                            <div className="space-y-1.5 py-1" data-testid={`rb-drill-${r.competency_code}`}>
                              {(r.indicators || []).map((i) => (
                                <div key={i.item_id} className="text-xs flex items-start gap-2">
                                  <Badge variant="outline" className="shrink-0 tabular-nums">
                                    {i.value === 'no_evidence' ? 'N/E' : i.value}
                                  </Badge>
                                  <span>
                                    {(i.focus_es || i.focus_en) && <b className="mr-1">{isES ? i.focus_es : i.focus_en}:</b>}
                                    {isES ? i.text_es : i.text_en}
                                  </span>
                                </div>
                              ))}
                              {(r.status === 'yellow' || r.status === 'red') && (
                                <Button size="sm" variant="outline" className="mt-1 h-7 text-xs" data-testid={`rb-handoff-${r.competency_code}`}
                                        onClick={async (ev) => {
                                          ev.stopPropagation();
                                          try {
                                            const { data: hd } = await api.post(`/admin/rb/benchmarks/${results.benchmark_id}/handoff/${r.competency_code}`, {});
                                            if (hd.already_exists) {
                                              toast.info(L('Esta brecha ya estaba en TalentTrain — no se duplicó.', 'This gap was already in TalentTrain — not duplicated.'));
                                            } else {
                                              toast.success(L('Necesidad de desarrollo enviada a TalentTrain (sin inscripción automática)', 'Development need handed off to TalentTrain (no automatic enrollment)'));
                                            }
                                          } catch (e2) { toast.error(e2?.response?.data?.detail || 'Error'); }
                                        }}>
                                  {L('Enviar a TalentTrain', 'Send to TalentTrain')}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );
}
